import { useEffect, useMemo, useState } from 'react';
import { addDays, endOfWeek, isSameDay, isWithinInterval, startOfWeek } from 'date-fns';
import { api } from 'services/api';
import { Guide } from 'types/guide';
import { Paginated } from 'types/paginated';
import { DAY_COUNT, END_HOUR, START_HOUR, getGuideDate } from '../agenda';

// Consultas da semana atual, com a busca aplicada e os totais do dia.
export function useWeekAgenda(searchTerm: string) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const weekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const weekEnd = useMemo(() => endOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const weekDays = useMemo(
    () => Array.from({ length: DAY_COUNT }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  );
  const hours = useMemo(() => Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, index) => START_HOUR + index), []);

  useEffect(() => {
    let isMounted = true;

    const fetchWeekGuides = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const response = await api.get<Paginated<Guide[]>>('/guides', {
          params: {
            date_filter: 'appointment_date',
            initial_date: weekStart,
            final_date: weekEnd,
            page: 1,
            rows: 96,
          },
        });

        if (!isMounted) return;

        const sortedGuides = response.data.items
          .filter(guide => guide.status !== 'deleted')
          .sort((firstGuide, secondGuide) => getGuideDate(firstGuide).getTime() - getGuideDate(secondGuide).getTime());

        setGuides(sortedGuides);
      } catch {
        if (isMounted) setHasError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchWeekGuides();

    return () => {
      isMounted = false;
    };
  }, [weekEnd, weekStart]);

  const filteredGuides = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) return guides;

    return guides.filter(guide => {
      const searchableText = [
        guide.patient?.name,
        guide.doctor?.name,
        guide.procedure?.name,
        guide.marker?.name,
        guide.id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [guides, searchTerm]);

  const todayGuides = useMemo(() => guides.filter(guide => isSameDay(getGuideDate(guide), new Date())), [guides]);

  const nextGuide = useMemo(() => {
    const now = new Date();

    return guides.find(guide =>
      isWithinInterval(getGuideDate(guide), {
        start: now,
        end: weekEnd,
      }),
    );
  }, [guides, weekEnd]);

  const totalValue = useMemo(
    () => todayGuides.reduce((total, guide) => total + Number(guide.value_total || 0), 0),
    [todayGuides],
  );

  const guideCountLabel = `${todayGuides.length} ${todayGuides.length === 1 ? 'consulta' : 'consultas'}`;

  return { isLoading, hasError, weekDays, hours, filteredGuides, nextGuide, totalValue, guideCountLabel };
}
